import { keys } from '../keys';
import {
  createBillingConfig,
  PriceInterval,
  PriceModel,
  PriceType
} from './schema';

enum Feature {
  AutomaticReceiptCapture = 'Automatische Belegerfassung',
  AIBookings = 'KI-gestützte Buchhaltung',
  IncludedBookings = '20 KI-Buchungen inklusive / Monat',
  UsageAfterLimit = 'Danach 0,05€ pro KI-Buchung',
  LifetimeUsageAfterLimit = 'Zahle was du nutzt. 0,04€ pro KI-Buchung',
  VATFilingThreeClicks = 'Umsatzsteuer-Voranmeldung mit 3 Klicks',
  DashboardReporting = 'Dashboard & Auswertungen',
  NewFeaturesIncluded = 'Alle neuen Features inklusive',
  EarlyAdopterPerks = 'Early Adopter — Sei Teil unserer Entwicklung'
}

const currency = 'EUR';

export const billingConfig = createBillingConfig({
  products: [
    {
      id: 'pro',
      name: 'Pro',
      description: 'Perfekt für die meisten Teams. 20 KI-Buchungen inklusive pro Monat.',
      label: 'Pro Plan',
      enableDiscounts: true,
      disabled: true,
      features: [
        Feature.AutomaticReceiptCapture,
        Feature.AIBookings,
        Feature.VATFilingThreeClicks,
        Feature.DashboardReporting,
        Feature.NewFeaturesIncluded,
        Feature.IncludedBookings,
        Feature.UsageAfterLimit
      ],
      plans: [
        {
          id: 'plan-pro-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PRO_MONTH_ID ||
                'price-pro-month-id',
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 20,
              currency
            }
          ]
        },
        {
          id: 'plan-pro-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PRO_YEAR_ID ||
                'price-pro-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 168,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'usage-billing-pro',
      name: 'Pro Usage Billing',
      description: 'Nutzungskontrollen für Pro-Abonnements.',
      label: 'Pro Nutzungskontrollen',
      hidden: true,
      features: [
        Feature.UsageAfterLimit
      ],
      plans: [
        {
          id: 'usage-billing-pro-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PRO_MONTH_METERED_ID ||
                'price-pro-month-metered-id',
              type: PriceType.Recurring,
              interval: PriceInterval.Month,
              model: PriceModel.Metered,
              cost: 0,
              currency: currency,
              meter: {
                id:
                  keys().NEXT_PUBLIC_BILLING_METER_PRO_MONTH_ID ||
                  'meter-pro-month-id',
                unit: 'KI Buchungen',
                tiers: [
                  { upTo: 0, cost: 0 },
                  { upTo: Infinity, cost: 0.05 }
                ]
              }
            }
          ]
        },
        {
          id: 'usage-billing-pro-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PRO_YEAR_METERED_ID ||
                'price-pro-year-metered-id',
              type: PriceType.Recurring,
              interval: PriceInterval.Month,
              model: PriceModel.Metered,
              cost: 0,
              currency: currency,
              meter: {
                id:
                  keys().NEXT_PUBLIC_BILLING_METER_PRO_YEAR_ID ||
                  'meter-pro-year-id',
                unit: 'KI Buchungen',
                tiers: [
                  { upTo: 0, cost: 0 },
                  { upTo: Infinity, cost: 0.05 }
                ]
              }
            }
          ]
        }
      ]
    },


    {
      id: 'lifetime',
      name: 'Lifetime',
      description: '🎉 Einmalige Zahlung für lebenslangen Zugang!',
      label: 'Lifetime Deal - Limitiert',
      enableDiscounts: true,
      recommended: true,
      features: [
        Feature.AutomaticReceiptCapture,
        Feature.AIBookings,
        Feature.VATFilingThreeClicks,
        Feature.DashboardReporting,
        Feature.NewFeaturesIncluded,
        Feature.LifetimeUsageAfterLimit,
        Feature.EarlyAdopterPerks
      ],
      plans: [
        {
          id: 'lifetime-onetime',
          displayIntervals: [PriceInterval.Month, PriceInterval.Year],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_LIFETIME_ID ||
                'price-lifetime-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 149,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'usage-billing-lifetime',
      name: 'Lifetime Usage Billing',
      description: 'Nutzungskontrollen für Lifetime-Abonnements.',
      label: 'Lifetime Nutzungskontrollen',
      hidden: true,
      features: [
        Feature.LifetimeUsageAfterLimit
      ],
      plans: [
        {
          id: 'usage-billing-lifetime-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_LIFETIME_METERED_ID ||
                'price-lifetime-metered-id',
              type: PriceType.Recurring,
              interval: PriceInterval.Month,
              model: PriceModel.Metered,
              cost: 0,
              currency: currency,
              meter: {
                id:
                  keys().NEXT_PUBLIC_BILLING_METER_LIFETIME_ID ||
                  'meter-lifetime-id',
                unit: 'KI Buchungen',
                tiers: [
                  { upTo: 0, cost: 0 },
                  { upTo: Infinity, cost: 0.04 }
                ]
              }
            }
          ]
        }
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Ideal für maßgeschneiderte Anforderungen.',
      label: 'Kontakt aufnehmen',
      isEnterprise: true,
      features: [
        Feature.AutomaticReceiptCapture,
        Feature.AIBookings,
        Feature.VATFilingThreeClicks,
        Feature.DashboardReporting,
        Feature.NewFeaturesIncluded
      ],
      plans: [
        {
          id: 'plan-enterprise-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_ENTERPRISE_MONTH_ID ||
                'price-enterprise-month-id',
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 39,
              currency
            }
          ]
        },
        {
          id: 'plan-enterprise-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_ENTERPRISE_YEAR_ID ||
                'price-enterprise-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 399,
              currency
            }
          ]
        }
      ]
    }
  ]
});

export const billingConfigDisplayIntervals = Array.from(
  new Set(
    billingConfig.products
      .filter((product) => !product.hidden)
      .flatMap((product) =>
        product.plans.flatMap((plan) => plan.displayIntervals)
      )
      .filter(Boolean)
  )
) as PriceInterval[];
